import { computed, ref, watch } from 'vue'

import type { MarketCountryCode } from '@backmarket/http-api/src/api-models/MarketCountryCode'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { useDebounceFn } from '@backmarket/utils/composables/useDebouncedFn'
import { removeEmptyValuesInObject } from '@backmarket/utils/object/removeEmptyValuesInObject'

import {
  type CATEGORIES_ENUM,
  type COMPETITION_LEVELS_ENUM,
  ORDER_BY,
  type ORDER_BY_ENUM,
  type Opportunities,
  type QUOTES_GRADES_ENUM,
} from '~/api/quotes/types'

import translations from './useFetchSourcingOpportunities.translations'

export interface UseFetchSourcingOpportunitiesOptions {
  marketCodes?: MarketCountryCode[]
  model?: string
  brand?: string
  grades?: QUOTES_GRADES_ENUM[]
  categories?: CATEGORIES_ENUM[]
  pageNumber?: number
  pageSize?: string
  orderBy?: ORDER_BY_ENUM
  ascending?: boolean
  competitionLevels?: COMPETITION_LEVELS_ENUM[]
}

export const defaultOptions = {
  marketCodes: [],
  model: '',
  brand: '',
  grades: [],
  categories: [],
  pageNumber: 1,
  pageSize: '10',
  orderBy: ORDER_BY.PROFIT_POTENTIAL,
  ascending: false,
  competitionLevels: [],
}
export function useFetchSourcingOpportunities(
  options: UseFetchSourcingOpportunitiesOptions = {},
) {
  const $httpFetch = useHttpFetch()
  const { openErrorToast } = useTheToast()
  const i18n = useI18n()

  // Create reactive refs for all query params
  const marketCodes = ref<MarketCountryCode[]>(
    options.marketCodes ?? defaultOptions.marketCodes,
  )
  const model = ref<string>(options.model ?? defaultOptions.model)
  const brand = ref<string>(options.brand ?? defaultOptions.brand)
  const grades = ref<QUOTES_GRADES_ENUM[]>(
    options.grades ?? defaultOptions.grades,
  )
  const categories = ref<CATEGORIES_ENUM[]>(
    options.categories ?? defaultOptions.categories,
  )
  const pageSize = ref<string>(options.pageSize ?? defaultOptions.pageSize)
  const orderBy = ref<ORDER_BY_ENUM>(options.orderBy ?? defaultOptions.orderBy)
  const ascending = ref<boolean>(options.ascending ?? defaultOptions.ascending)
  const pageNumber = ref<number>(
    options.pageNumber ?? defaultOptions.pageNumber,
  )
  const competitionLevels = ref<COMPETITION_LEVELS_ENUM[]>(
    options.competitionLevels ?? defaultOptions.competitionLevels,
  )

  const opportunities = ref<Opportunities | null>(null)
  const error = ref<Error | null>(null)
  const status = ref<string | null>('pending')

  // Compute the query object from reactive refs
  const query = computed(() =>
    removeEmptyValuesInObject({
      marketCodes: marketCodes.value,
      model: model.value,
      brand: brand.value,
      grades: grades.value,
      categories: categories.value,
      pageSize: pageSize.value,
      orderBy: orderBy.value,
      ascending: ascending.value,
      pageNumber: pageNumber.value,
      competitionLevels: competitionLevels.value,
    }),
  )

  function reset() {
    marketCodes.value = defaultOptions.marketCodes
    categories.value = defaultOptions.categories
    grades.value = defaultOptions.grades
    orderBy.value = defaultOptions.orderBy
    ascending.value = defaultOptions.ascending
    brand.value = defaultOptions.brand
    model.value = defaultOptions.model
    pageSize.value = defaultOptions.pageSize
    competitionLevels.value = defaultOptions.competitionLevels
  }

  async function fetch() {
    status.value = 'pending'
    try {
      opportunities.value = await $httpFetch(
        '/buyback-quotes/api/v1/sourcing-opportunities',
        {
          method: 'GET',
          query: query.value,
        },
      )
      status.value = 'success'
    } catch (err) {
      error.value = err as Error
      status.value = 'error'
    }
  }

  const debouncedFetch = useDebounceFn(fetch, 500)

  watch(
    [
      pageNumber,
      marketCodes,
      model,
      brand,
      grades,
      categories,
      pageSize,
      orderBy,
      ascending,
      competitionLevels,
    ],
    ([newPageNumber], [oldPageNumber]) => {
      if (newPageNumber === oldPageNumber) {
        if (pageNumber.value !== 1) {
          pageNumber.value = 1

          return
        }
      }
      void debouncedFetch()
    },
    { deep: true },
  )

  watch(error, (newError) => {
    if (newError) {
      openErrorToast({
        content: i18n(translations.errorGettingOpportunities),
      })
    }
  })

  void fetch()

  return {
    opportunities,
    error,
    status,

    marketCodes,
    model,
    brand,
    grades,
    categories,
    pageNumber,
    pageSize,
    orderBy,
    ascending,
    competitionLevels,

    fetch,
    reset,
  }
}
